import { ReactElement } from "react";
import {
    CART_CHANGE_ANALYZED,
    EMIT_CART_CLOSED,
    EMIT_CART_OPENED,
    EMIT_CART_UPDATED,
    IS_METAL_BIRD_SHOP,
} from "../../constants";
import { addDefaultCartToProductForm, handleCustomCheckout, hideShopifyCartElements } from "../../utilities";
import { ATTRIBUTE_KEY_SHORTCODES, BundleTypes, WIDGET_POSITIONS_OPT } from "../constants";
import { ProductObj, VariantsObj } from "../Types";
import { BUNDLE_CONTAINER_PLACEHOLDER_IDS, MIX_MATCH_MOBILE_MAX_WIDTH, CART_COUNT_SELECTORS } from "./constants";
import { handleSunriseAboveCart } from "./themeSpecficFunctions";
import config, { BACKEND_URL } from "../../../config";

const appName = config.appName;

export async function handleBundleCheckout({
    items,
    freeItems,
    addOnItems = [],
    formId,
    token,
    boxQuantity,
    redirectOptions,
    showPopupOnCartRedirect,
    setShowCheckoutModal,
    setLoadingState,
    bundleType,
    abVariantUuid,
    checkoutLabels,
}: {
    items: any[];
    freeItems: any[];
    addOnItems?: any[];
    formId: string;
    token: string;
    boxQuantity?: number;
    redirectOptions: string;
    showPopupOnCartRedirect: boolean;
    setShowCheckoutModal: (show: boolean) => void;
    setLoadingState: (loading: boolean) => void;
    bundleType: string;
    abVariantUuid?: string;
    checkoutLabels?: {
        checkout_box_label: string;
        checkout_discount_label: string;
    };
}) {
    const shop = window.kefi_bundles.shop;
    const searchParams = new URLSearchParams(window.location.search);

    try {
        const response = await fetch(`${BACKEND_URL}/public/${appName}/bundles/get-checkout-url?shop=${shop}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                products: items,
                addon_items: addOnItems,
                free_gifts: freeItems,
                form_id: formId,
                box_quantity: boxQuantity,
                bundle_type: bundleType,
                token: token,
                b2b_customer: window.kefi_bundles?.customer_b2b || false,
                variant_uuid: abVariantUuid,
                checkout_labels: checkoutLabels,
            }),
        });
        const data = await response.json();
        if (response.status !== 200) {
            setLoadingState(false);
            throw new Error(`Backend request failed with status ${response.status}`);
        }

        const { lineData, attributes } = data.data;

        const itemsToAdd: any[] = [];

        lineData.forEach((item: any) => {
            const variantId = item.merchandiseId.split("/").pop(); // extract numeric ID
            const quantity = item.quantity || 1;

            // Combine all attributes as properties
            const properties: Record<string, string> = {};
            item.attributes.forEach((attr: any) => {
                properties[attr.key] = attr.value;
            });

            // Attach ALL query params
            if (["prepped-mealz.myshopify.com"].includes(window?.kefi_bundles?.shop)) {
                searchParams.forEach((value, key) => {
                    properties[key] = value;
                });
            }

            itemsToAdd.push({
                id: Number(variantId),
                quantity,
                properties,
            });
        });

        const cartAttributes: Record<string, string> = {};
        if (Array.isArray(attributes)) {
            attributes.forEach((attr: any) => {
                cartAttributes[attr.key] = attr.value;
            });
        }

        if (redirectOptions === "cart" && showPopupOnCartRedirect) {
            window.kefi_skip_cart_open = true;
        }

        // For neesh-perfumes.myshopify.com, this bundle is given away free (only shipping is charged).
        // They only want one instance of it in the cart at a time, so any existing free (price 0) line
        // items are removed right before the new bundle items are added.
        if (shop === "neesh-perfumes.myshopify.com" && formId === "6a7391ac517ab02588f64d8d") {
            await removeZeroPriceLineItemsFromCart();
        }

        const fetchMethod = redirectOptions === "cart" ? fetch : window.originalFetch;
        const addResponse = await fetchMethod("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: itemsToAdd, attributes: cartAttributes }),
        });

        const addData = await addResponse.json();
        console._log("Added to cart:", addData);

        if (addResponse.status == 200) {
            setLoadingState(false);

            if (redirectOptions == "cart") {
                if (showPopupOnCartRedirect) {
                    setShowCheckoutModal(true);
                } else {
                    openStoreCart();
                }
            } else {
                document.dispatchEvent(
                    new CustomEvent(CART_CHANGE_ANALYZED, {
                        detail: { source: "CartDrawer", isCartLinesUpdating: false },
                    }),
                );
                handleCustomCheckout();
            }
        } else {
            setLoadingState(false);
            throw new Error(`Cart addition failed with status ${addResponse.status}`);
        }
    } catch (error) {
        setLoadingState(false);
        console.error("Error fetching checkout URL:", error);
    }
}

// Removes all free (price 0) line items currently in the cart, e.g. for shops that
// give a bundle away for free and only want one instance of it in the cart at a time.
async function removeZeroPriceLineItemsFromCart() {
    try {
        const cartResponse = await fetch("/cart.js");
        const cart = await cartResponse.json();
        const freeLines = (cart.items || [])
            .map((item: any, index: number) => ({ line: index + 1, price: item.price }))
            .filter((item: any) => item.price === 0);

        // Remove from the highest line number down so earlier removals don't shift
        // the line numbers of items still pending removal.
        for (const { line } of freeLines.reverse()) {
            await fetch("/cart/change.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ line, quantity: 0 }),
            });
        }
    } catch (error) {
        console.error("Error removing free line items from cart:", error);
    }
}

export function hideProductPrice() {
    const priceClass = "price";

    const priceNodes = document.getElementsByClassName(priceClass);

    for (const _node of priceNodes) {
        const node = _node as HTMLElement;
        if (node) node.style.display = "none";
    }
}

export function handleOptionChange(e: Event, hiddenInput: HTMLInputElement) {
    const event = e as CustomEvent;

    if (event.detail) {
        const { selected_options, not_found } = event.detail;
        hiddenInput.value = JSON.stringify(selected_options);
        const buttons = hiddenInput.parentElement?.getElementsByTagName("button");

        if (buttons)
            for (const button of buttons) {
                if (Object.values(not_found).flat().length !== 0) {
                    button.disabled = true;
                    button.setAttribute("actual-html", button.innerHTML);
                    button.innerText = "Unavailable";
                } else {
                    const html = button.getAttribute("actual-html");
                    button.disabled = false;
                    if (html) button.innerHTML = html;
                }
            }
    }
}

export const getAddToCartClasses = () => {
    const form = document.querySelector(`[action^='${window.kefi_bundles.routes.cart_add_url}']`);
    if (form) {
        try {
            const addToCartBtn = form.querySelector('*[type="submit"]');

            window.kefi_bundles.addToCartClasses = addToCartBtn?.classList.value || "";
        } catch (error) {
            console.error("Error getting add to cart classes:", error);
        }
    }
};

export const getPrimaryCtaClasses = () => {
    const form = document.querySelector(`[action^='${window.kefi_bundles.routes.cart_add_url}']`);
    if (form) {
        try {
            const primaryCtaButton = form.querySelector('*[type="button"]');
            window.kefi_bundles.primaryCtaClasses = primaryCtaButton?.classList.value || "";
        } catch (error) {
            console.error("Error getting add to cart classes:", error);
        }
    }
};

export const getParentElement = (config: any, targetForm: Element): Element | null => {
    if (config?.bundle_type == BundleTypes.FBT) {
        return getElementWRTPosition(
            config?.new_bundle_position || config?.bundles_settings?.bundle_position,
            config?.new_custom_selector || config?.bundles_settings?.custom_selector,
            config?.bundle_type,
            targetForm,
            config.bundle_embed_id,
        );
    }

    return getElementWRTPosition(
        config?.bundles_settings?.bundle_position,
        config?.bundles_settings?.custom_selector,
        config?.bundle_type,
        targetForm,
        config.bundle_embed_id,
    );
};

const getElementWRTPosition = (
    bundle_position: string = "",
    custom_selector: string = "",
    bundle_type: string,
    targetForm: Element,
    embed_id: string,
): Element | null => {
    try {
        // @ts-expect-error hack
        const appBlock = document.getElementById(BUNDLE_CONTAINER_PLACEHOLDER_IDS[bundle_type]);
        let element = checkForEmbedId(embed_id);
        if (element) return element;
        if (bundle_type == BundleTypes.BUNDLE_BUILDER) {
            element =
                document.querySelector("main") ||
                document.querySelector(".page-main-content") ||
                document.querySelector(".global-wrapper") ||
                document.querySelector("#main-content");
            if (element) {
                element.innerHTML = "";
            }
        } else if (appBlock) {
            element = appBlock;
        } else if (
            bundle_position == WIDGET_POSITIONS_OPT.ABOVE_ELEMENT ||
            bundle_position == WIDGET_POSITIONS_OPT.BELOW_ELEMENT
        ) {
            const custom_element = custom_selector ? document.querySelector(custom_selector) : null;
            if (custom_element) {
                element = document.createElement("div");
                if (bundle_position == WIDGET_POSITIONS_OPT.ABOVE_ELEMENT) {
                    custom_element?.parentNode?.insertBefore(element, custom_element);
                } else {
                    custom_element?.after(element);
                }
            }
        } else if (
            bundle_position == WIDGET_POSITIONS_OPT.ABOVE_CART ||
            bundle_position == WIDGET_POSITIONS_OPT.BELOW_CART
        ) {
            element = document.createElement("div");
            if (
                bundle_position == WIDGET_POSITIONS_OPT.ABOVE_CART &&
                window.kefi_bundles.shop != "ohluckycl.myshopify.com"
            ) {
                if (window.Shopify.theme.schema_name == "Sunrise") {
                    handleSunriseAboveCart(element);
                } else targetForm?.parentNode?.insertBefore(element, targetForm);
            } else {
                targetForm.after(element);
            }
        } else if (bundle_position == WIDGET_POSITIONS_OPT.FBT_DEFAULT) {
            const main = document.querySelector("main");
            element =
                document.querySelector("#shopify-section-product-template") ||
                (main
                    ? Array.from(main.children)
                          .reverse()
                          ?.find((child) => child.tagName.toLowerCase() !== "script") || null
                    : null);
        } else {
            element = document.createElement("div");

            targetForm.after(element);
        }

        return element;
    } catch (error) {
        return null;
    }
};
export const groupBundlesByGroupNum = (
    containers: Array<{
        parent: Element | null;
        children: ReactElement[];
        groupNum: number;
    }>,
) => {
    const grouped = new Map<number, { parent: Element; children: ReactElement[] }>();

    containers.forEach(({ parent, children, groupNum }) => {
        if (!parent) return;

        if (!grouped.has(groupNum)) {
            grouped.set(groupNum, {
                parent,
                children: [...children],
            });
            return;
        }

        grouped.get(groupNum)!.children.push(...children);
    });

    return grouped;
};

export const applyPageWidthClass = (container: Element) => {
    if (!container.closest(".page-width")) {
        container.classList.add("page-width");
    }
    if (!container.closest(".t4s-container")) {
        // for user  tnbdv7-qz.myshopify.com
        container.classList.add("t4s-container");
    }
    if (!container.closest(".cntr")) {
        // for Blum theme
        container.classList.add("ctnr");
    }
};

export const checkForEmbedId = (id: string) => document.querySelector(`[data-kefi-bundle-id="${id}"]`);

export const checkForBundleId = (id: string) => document.querySelector(`[data-kefi-bundle-id="${id}"]`);

export const hideElementsBySelector = (selectorString: string): void => {
    if (!selectorString) return;
    const selectors = selectorString.split(",").map((s) => s.trim());
    selectors.forEach((selector) => {
        const elements = document.querySelectorAll<HTMLElement>(selector);
        elements.forEach((el) => {
            el.style.display = "none";
            el.style.height = "0px";
            el.style.width = "0px";
            el.style.overflow = "hidden";
            el.style.transform = "scale(0)";
        });
    });
};

export const showElementsBySelector = (selectorString: string): void => {
    if (!selectorString) return;
    const selectors = selectorString.split(",").map((s) => s.trim());
    selectors.forEach((selector) => {
        const elements = document.querySelectorAll<HTMLElement>(selector);
        elements.forEach((el) => {
            el.style.display = "";
            el.style.height = "";
            el.style.width = "";
            el.style.overflow = "";
        });
    });
};
export function overrideDefaultButton() {
    const cartLinks = Array.from(document.querySelectorAll("a")).filter((a) => {
        try {
            const url = new URL(a.href, location.origin);
            return url.pathname === window.kefi_bundles.routes.cart_url || a.hasAttribute("data-cart-sidebar");
        } catch (e) {
            return false;
        }
    });
    const element = (document?.querySelector("#cart-icon-bubble") ||
        document.querySelector(".header__icon.header__icon--cart") ||
        document.querySelector("#headerCartStatus") ||
        document?.querySelector(".site-header__icon.site-header__cart") ||
        document?.querySelector(".cart-drawer > button")) as HTMLAnchorElement;

    cartLinks.push(element);
    hideShopifyCartElements();
    addDefaultCartToProductForm();
    if (IS_METAL_BIRD_SHOP) metalBirdChanges();
    if (
        window.kefi_bundles.shop == "beautybay-3691.myshopify.com" ||
        window.kefi_bundles.shop == "kiin-baby.myshopify.com"
    ) {
        lunaTologyShopChanges();
    }
    if (window.kefi_bundles.shop == "5335df-00.myshopify.com") ecowareChanges();
    if (window.kefi_bundles.shop == "59897e-0d.myshopify.com") gardeningOnCountryChanges();
    if (window.kefi_bundles.shop == "47fb80.myshopify.com") geraldineportShopChanges();
    if (window.kefi_bundles.shop == "re1iw3-bs.myshopify.com") aussieGolfShopChanges();
    if (window.kefi_bundles.shop == "laenia.myshopify.com") laeniaShopChanges();
    if (window.kefi_bundles.shop == "puur-cl.myshopify.com") puurbottleChanges();
    if (window.kefi_bundles.shop == "trandafir-criogenat-ro.myshopify.com") trandafirShopChanges();
    if (window.kefi_bundles.shop == "evvvgg-b1.myshopify.com") atulyamChanges();
    if (window.kefi_bundles.shop == "jardosh-studio.myshopify.com") jardoshStudioChanges();
    if (window.kefi_bundles.shop == "eyemuse.myshopify.com") eyeMuseChanges();
    cartLinks.forEach((link) => {
        if (link) {
            // const cloneLink = link.cloneNode(true) as HTMLAnchorElement;
            // link.style.display = "none";
            // link.insertAdjacentElement("afterend", cloneLink);
            link.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                if (window.openMyCart) window.openMyCart(true);
            });
        }
    });

    document.addEventListener(EMIT_CART_UPDATED, (e: any) => {
        const cartItemCount = e.detail.cartData?.item_count || 0;
        try {
            updateCartCountElements(cartItemCount);
        } catch (error) {
            console._log("Cannot update cart count bubble.");
        }
    });
}

function updateCartCountElements(count: number | string) {
    const normalizedCount = Number(count) || 0;

    CART_COUNT_SELECTORS.forEach((config) => {
        const { selector, content, selectAll, onCountZero, onCountNonZero, onNotFound } = config;

        let elements: any[] = [];

        try {
            elements = selectAll ? Array.from(document.querySelectorAll(selector)) : [document.querySelector(selector)];
        } catch (err) {
            console.warn("Invalid selector:", selector);
            return;
        }

        const validElements = elements.filter(Boolean);

        if (!validElements.length) {
            onNotFound?.(normalizedCount);
            return;
        }

        validElements.forEach((el: HTMLElement) => {
            switch (content.type) {
                case "innerHtml":
                    el.innerHTML = String(normalizedCount);
                    break;

                case "dataAttribute":
                    if (content.attributeName) {
                        el.setAttribute(content.attributeName, String(normalizedCount));
                    }
                    break;

                case "noContent":
                    break;
            }

            if (normalizedCount === 0) {
                onCountZero?.(el);
            } else {
                onCountNonZero?.(el);
            }
        });
    });
}

export const isThirdPartyCheckout = () => {
    return !!window.gokwikSdk?.initCheckout || !!window.Shopflo?.openCheckout;
};

export const isDesktopUIForMixMatch = (bundleWidth: number, bundle_type: string) => {
    return (
        MIX_MATCH_MOBILE_MAX_WIDTH < bundleWidth &&
        (BundleTypes.MIX_MATCH_SECTIONAL === bundle_type || BundleTypes.MIX_MATCH === bundle_type)
    );
};

export const openStoreCart = async (data?: any) => {
    const cart: any = document.querySelector("cart-drawer") || document.querySelector("cart-notification");

    try {
        if (window.kefi_cart_available) {
            document.dispatchEvent(
                new CustomEvent(CART_CHANGE_ANALYZED, {
                    detail: { isCartLinesUpdating: true },
                }),
            );
        } else if (window.kefi_bundles.shop == "d2f1bd.myshopify.com") {
            document.dispatchEvent(new CustomEvent("cart:refresh"));
            cart.show(true);
        } else if (window?.kefi_bundles?.shop == "tnbdv7-qz.myshopify.com") {
            document.dispatchEvent(new Event("cart:refresh:opend"));
        } else if (window.SLIDECART_OPEN && window.SLIDECART_STATE().settings.enabled) {
            window.SLIDECART_UPDATE(window.SLIDECART_OPEN);
        } else if (window.monster_refresh) {
            window.monster_refresh();
        } else if (window.handleFloCartBtn) {
            window.handleFloCartBtn();
        } else if (window.MinimogTheme?.Cart) {
            await window.MinimogTheme.Cart.refreshCart();
            window.MinimogTheme.Cart.updateCartCount(window.MinimogTheme.Cart.cart);
            await window.MinimogTheme.Cart.renderNewCart();
            window.MinimogTheme.Cart.openCartDrawer();
        } else if (window?.StickyCart) {
            await window?.STICKY_CART_UPDATE();
            await window?.STICKY_CART_OPEN();
        } else if (window?.unicornActiveCart && window?.unicornOpenCart) {
            window.unicornRefreshCart();
            window.unicornOpenCart();
        } else if (window.kachingCartApi) {
            window.kachingCartApi?.openCart({ refetchCart: true });
        } else if (customElements.get("cart-element")) {
            const cartElement: any = customElements.get("cart-element");
            cartElement?.updateAllHtml(data.sections);
            const drawerEle: any = customElements.get("drawer-element");
            drawerEle?.open("right", "cart-drawer", document.getElementsByClassName("header--cart"));
            document.querySelector(".layout--viewport")?.setAttribute("data-cart-empty", "false");
        } else if (window.kefi_bundles.shop == "rajkot-patola-art.myshopify.com") {
            // do nothing
        } else if (
            window.kefi_bundles.shop == "msg-september2025-entwicklungsumgebung.myshopify.com" ||
            window.kefi_bundles.shop == "mein-schoener-garten-shop.myshopify.com"
        ) {
            document.dispatchEvent(new CustomEvent("cart:refresh"));
            const cartProvider: any = document.querySelector("cart-provider");
            await cartProvider?._cartRefresh();

            const cartModal: any = document.querySelector("modal-container#cart-drawer");
            cartModal.dispatchEvent(window.Global.DOMEvents.MODAL_OPEN);
        } else if (window.kefi_bundles.shop == "ullenboombaby.myshopify.com") {
            document.dispatchEvent(new CustomEvent("cart:refresh"));
            const cartDrawer: any = document.querySelector("cart-drawer");
            if (cartDrawer) {
                cartDrawer?.show();
            }
        } else if (window.kefi_bundles.shop == "the-beaded-space.myshopify.com") {
            (window as any)?.refreshCart(true);
        } else if (window.kefi_bundles.shop == "2pstrength.myshopify.com") {
            const cartDrawer: any = document.querySelector("cart-drawer");
            if (cartDrawer) {
                await cartDrawer?._onCartRefresh?.();
                cartDrawer.setAttribute("open", "");
            }
        }
        // else if (window.kefi_bundles.shop == "e3h3ai-wx.myshopify.com") {
        //     document.dispatchEvent(
        //         new CustomEvent("cart:update", {
        //             bubbles: true,
        //             detail: {
        //                 resource: cart,
        //                 sourceId: "bundle-app",
        //                 data: {},
        //             },
        //         }),
        //     );
        //     document.querySelector("cart-drawer-component")?.open?.();
        // }
        else if (window.kefi_bundles.shop == "skinseen1.myshopify.com") {
            const quickCart = window.Shopify.theme.sections.instances.find((s) => s.id?.includes("__quick-cart"));

            quickCart?.refreshQuickCart?.();

            const headerSectionId = window.Shopify.theme.sections.instances.find((s) => s.id?.includes("__header"))?.id;

            if (headerSectionId) {
                fetch(`/?section_id=${headerSectionId}`)
                    .then((r) => r.text())
                    .then((html) => {
                        const temp = document.createElement("div");
                        temp.innerHTML = html;

                        const selector = `[data-section-id="${headerSectionId}"]`;

                        const newHeader = temp.querySelector(selector);
                        const currentHeader = document.querySelector(selector);

                        if (newHeader && currentHeader) {
                            currentHeader.innerHTML = newHeader.innerHTML;
                        }
                    });
            }

            quickCart?.openQuickCart?.();
        } else if ((window as any)?.IcartAddCustomEventListener || (window as any)?.icartDrawerOpn) {
            // iCart slide cart support
            openICartSlideCart();
        } else if (window.kefi_bundles.shop == "extnutpro.myshopify.com") {
            // const cartDrawer = document.querySelector("#t4s-mini_cart");
            // if (cartDrawer) {
            //     cartDrawer?.setAttribute("aria-hidden", "false");
            // }
            if (window.kefi_bundles.localization.locale == "ar") {
                document.querySelector('[href="/ar/cart"]')?.click();
            } else {
                document.querySelector('[href="/cart"]')?.click();
            }

            document.dispatchEvent(new CustomEvent("cart:refresh"));
            // document.dispatchEvent(new CustomEvent("cart:open"));
        } else {
            cart.renderContents({ ...data, key: data.items[0].key });
            if (cart.classList.contains("is-empty")) cart.classList.remove("is-empty");
        }
    } catch (err) {
        window.location = window?.kefi_bundles?.routes?.cart_url as unknown as Location; // old line
        console.error(err); // old line
    }
};

// Opens iCart slide cart by calling iCart event functions (retries up to 5 times)
const openICartSlideCart = () => {
    let retries = 0;
    const maxRetries = 5;

    const tryOpenCart = () => {
        const hasICart =
            typeof (window as any)?.icartSideCartOpenEvent === "function" ||
            typeof (window as any)?.icartCartActivityEvent === "function";

        if (hasICart) {
            if (typeof (window as any)?.icartSideCartOpenEvent === "function") {
                (window as any).icartSideCartOpenEvent({});
            }
            if (typeof (window as any)?.icartCartActivityEvent === "function") {
                (window as any).icartCartActivityEvent();
            }
        } else if (retries < maxRetries) {
            retries++;
            setTimeout(tryOpenCart, 500);
        }
    };

    tryOpenCart();
};

const getPropertiesObj = (
    bType: string,
    selected: VariantsObj,
    p: ProductObj,
    properties: any,
    bundleFormId: string,
    mnmSectionalMessage?: string,
) => {
    let result: any = {};

    switch (bType) {
        case BundleTypes.VOLUME_DISCOUNT:
            if (!selected?.free_gift) {
                result = {
                    [ATTRIBUTE_KEY_SHORTCODES.__volume]: {
                        [ATTRIBUTE_KEY_SHORTCODES.form_id]: bundleFormId,
                    },
                };
            }
            if (p?.productProperties) {
                result = {
                    [ATTRIBUTE_KEY_SHORTCODES.__volume]: {
                        [ATTRIBUTE_KEY_SHORTCODES.form_id]: bundleFormId,
                        ...p.productProperties,
                    },
                };
            }
            break;

        case BundleTypes.FBT:
            result = {
                [ATTRIBUTE_KEY_SHORTCODES.__fbt]: {
                    [ATTRIBUTE_KEY_SHORTCODES.form_id]: bundleFormId,
                    [ATTRIBUTE_KEY_SHORTCODES.required_product]: p?.required_product ? 1 : 0,
                },
            };
            break;

        case BundleTypes.PRODUCT_ADDONS:
            result = {
                [ATTRIBUTE_KEY_SHORTCODES.__add_on]: {
                    [ATTRIBUTE_KEY_SHORTCODES.form_id]: bundleFormId,
                    [ATTRIBUTE_KEY_SHORTCODES.required_product]: p?.required_product ? 1 : 0,
                },
            };
            break;

        case BundleTypes.PRODUCT_ADDONS_POPUP:
            result = {
                [ATTRIBUTE_KEY_SHORTCODES.__add_on_popup]: {
                    [ATTRIBUTE_KEY_SHORTCODES.form_id]: bundleFormId,
                    [ATTRIBUTE_KEY_SHORTCODES.required_product]: p?.required_product ? 1 : 0,
                },
            };
            break;

        case BundleTypes.MIX_MATCH_SECTIONAL:
            if (mnmSectionalMessage?.trim()) {
                result = {
                    ...properties,
                    [ATTRIBUTE_KEY_SHORTCODES.__mnm_message]: mnmSectionalMessage,
                };
            }
            break;

        case BundleTypes.STANDARD:
            result = {
                ...properties,
                [ATTRIBUTE_KEY_SHORTCODES.__standard]: {
                    [ATTRIBUTE_KEY_SHORTCODES.form_id]: bundleFormId,
                },
            };
            break;

        case BundleTypes.MIX_MATCH:
            result = {
                ...properties,
                [ATTRIBUTE_KEY_SHORTCODES.__mix_match]: {
                    [ATTRIBUTE_KEY_SHORTCODES.form_id]: bundleFormId,
                },
            };
            break;

        case BundleTypes.BUY_N:
            result = {
                [ATTRIBUTE_KEY_SHORTCODES.__buy_n]: {
                    [ATTRIBUTE_KEY_SHORTCODES.form_id]: bundleFormId,
                },
            };
            break;

        case BundleTypes.BXGY:
            result = {
                ...properties,
                [ATTRIBUTE_KEY_SHORTCODES.__bxgy]: {
                    [ATTRIBUTE_KEY_SHORTCODES.form_id]: bundleFormId,
                },
            };
            break;

        default:
            result = properties;
    }

    return result;
};

export async function addBundleToCart(
    products: any,
    properties?: any,
    bundleFormId?: string,
    redirectToCheckout?: boolean,
    bType?: string,
    mnmSectionalMessage?: string,
) {
    console._log(products);
    const items: Array<object> = [];

    products.forEach((p: ProductObj) => {
        p?.selectedVariants?.forEach((selected: VariantsObj) => {
            let propertiesObj = getPropertiesObj(bType, selected, p, properties, bundleFormId, mnmSectionalMessage);

            const hasOptions = properties && typeof properties === "object" && "_options" in properties;

            propertiesObj = hasOptions
                ? {
                      ...properties,
                      [ATTRIBUTE_KEY_SHORTCODES.__kbd]: propertiesObj,
                  }
                : {
                      [ATTRIBUTE_KEY_SHORTCODES.__kbd]: propertiesObj,
                  };

            const id = +(selected?.id?.split("/")?.pop() as string);
            const selling_plan = +(selected?.selling_plan?.split("/")?.pop() as string);

            const item: any = {
                quantity: selected?.quantity ? +selected.quantity : 1,
                id: id,
                selling_plan: Number.isNaN(selling_plan) || !selling_plan ? "" : selling_plan,
                free: selected.free,
            };

            if (propertiesObj && Object.keys(propertiesObj).length > 0) {
                item.properties = propertiesObj;
            }

            items.push(item);
        });
    });

    if (bType === BundleTypes.MIX_MATCH) {
        const uniqueSetId = +new Date();
        const mainItems = items.filter((i: any) => !i.free);
        for (let index = 0; index < items.length; index++) {
            const item: any = items[index];

            if (!item?.properties?.__KF?.mnm_part_of) break;

            if (item.free) {
                delete item?.properties?.__KF?.mnm_part_of;

                continue;
            }

            item.properties.__KF.mnm_part_of = `${item.properties.__KF.mnm_part_of}|${uniqueSetId}|${index + 1}|${
                mainItems.length
            }`;
        }
    }

    const cart: any = document.querySelector("cart-drawer") || document.querySelector("cart-notification");

    items.forEach((item: any) => delete item.free);

    const formData = {
        items,
        sections: [],
    };

    if (cart && cart.getSectionsToRender) {
        formData.sections = cart.getSectionsToRender().map((s: any) => s.section || s.id || s.selector);
    }
    if (window.kefi_bundles.shop == "jbgst0-wa.myshopify.com") {
        const cartElement = customElements.get("cart-element");
        formData.sections = cartElement?.sections || [];
    }

    const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
    });

    if (res.status === 200) {
        const data = await res.json();
        if (redirectToCheckout) {
            handleCustomCheckout();
            return;
        }
        if (bType == BundleTypes.PRODUCT_ADDONS_POPUP) {
            window.isProductAddOnPopupModalOpen = false;
            const event = new Event("productAddOnPopupStateChange");
            window.dispatchEvent(event);
        }

        // For iCart: directly call drawer opening WITHOUT form submission (avoids duplicate product)
        if (typeof (window as any)?.IcartAddCustomEventListener !== "undefined") {
            // iCart detected - open drawer directly
            openICartSlideCart();
        } else {
            // Other cart systems
            openStoreCart(data);
        }

        return data;
    } else {
        const ele = document.getElementById("kefi_" + bundleFormId);
        const response = await res.json();

        let container = document.getElementById("error-message-container-" + bundleFormId);

        if (!container) {
            container = document.createElement("div");
            container.id = "error-message-container-" + bundleFormId;
            ele?.parentNode?.insertBefore(container, ele.nextSibling);
        }

        if (response.message.toLowerCase() == "parameter missing or invalid") {
            container.textContent = "No products added!";
        } else {
            container.textContent = response.message;
        }

        container.style.color = "red";
        container.style.fontSize = "15px";
        container.style.fontWeight = "500";
        container.style.paddingBottom = "10px";

        if (res.status === 422) {
            return {
                error: true,
                status: 422,
                message: response.message,
            };
        }
    }
}

export const metalBirdChanges = () => {
    const cartTriggerElements = document.querySelectorAll("[data-cart-toggle]");

    hideElementsBySelector("#dropdown-cart,.wrapper-overlay");

    cartTriggerElements.forEach((el) => {
        el.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            if (window.openMyCart) window.openMyCart(true);
        });
    });

    document.addEventListener(EMIT_CART_CLOSED, () => {
        document.body.style.overflow = "auto";
    });

    document.addEventListener(EMIT_CART_UPDATED, (e: any) => {
        const cartItemCount = e.detail.cartData?.item_count || "";

        const elements = document.getElementsByClassName("cart-count-number");
        if (elements) {
            for (const ele of elements) {
                if (cartItemCount) {
                    ele.innerHTML = cartItemCount;
                    const parent = ele.parentElement as HTMLElement;
                    if (parent) {
                        parent.style.display = "block";
                    }
                } else {
                    const parent = ele.parentElement as HTMLElement;
                    if (parent) {
                        parent.style.display = "none";
                    }
                }
            }
        }
    });
};

export const lunaTologyShopChanges = () => {
    const cartTriggerElements = document.querySelectorAll("#CartButton");

    cartTriggerElements.forEach((el) => {
        el.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            if (window.openMyCart) window.openMyCart(true);
        });
    });

    document.addEventListener(EMIT_CART_CLOSED, () => {
        if (window.Alpine) {
            window.Alpine.store("modals").close("cart");
        }
    });

    const waitForAlpine = () => {
        if (!window.Alpine) {
            requestAnimationFrame(waitForAlpine);
            return;
        }

        window.Alpine.effect(() => {
            const isSort = window.Alpine.store("modals").rightDrawer.contents === "sort";
            if (isSort) {
                const styles = document.getElementById("kefi-custom-cart-style");
                if (styles) styles.remove();
            } else {
                const existingStyle = document.getElementById("kefi-custom-cart-style");
                if (existingStyle) return;
                const style = document.createElement("style");
                style.id = "kefi-custom-cart-style";
                style.textContent = `
                    #modals-rightDrawer {
                        display: none !important;
                        height: 0px !important;
                        width: 0px !important;
                        overflow: hidden !important;
                        transform: scale(0) !important;
                    }
                `;
                document.head.appendChild(style);
            }
        });
    };

    waitForAlpine();
};

export const ecowareChanges = () => {
    document.addEventListener(EMIT_CART_CLOSED, () => {
        document.body.classList.remove("added__overlay", "overflow__hidden");
    });
};
export const eyeMuseChanges = () => {
    document.addEventListener(EMIT_CART_CLOSED, () => {
        document.querySelector("body")?.style.removeProperty("overflow");

        setTimeout(() => {
            document.dispatchEvent(
                new CustomEvent("theme:scroll:unlock", {
                    bubbles: !0,
                }),
            );
        }, 10);
    });

    document.addEventListener(EMIT_CART_OPENED, () => {
        setTimeout(() => {
            document.dispatchEvent(
                new CustomEvent("theme:scroll:unlock", {
                    bubbles: !0,
                }),
            );
        }, 10);
    });

    document.addEventListener(EMIT_CART_UPDATED, () => {
        setTimeout(() => {
            document.dispatchEvent(
                new CustomEvent("theme:scroll:unlock", {
                    bubbles: !0,
                }),
            );
        }, 10);
    });
};
export const gardeningOnCountryChanges = () => {
    const cartTriggerElements = document.querySelectorAll(".header-actions__cart-icon");

    const dialogElement = document.querySelector(".cart-drawer__dialog");
    if (dialogElement) dialogElement.remove();

    cartTriggerElements.forEach((el) => {
        el.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.openMyCart) window.openMyCart(true);
        });
    });

    document.addEventListener(EMIT_CART_CLOSED, () => {
        document.body.style.position = "unset";
    });
};
export const geraldineportShopChanges = () => {
    const cartTriggerElements = document.querySelectorAll(".header__minicart");

    const miniCartWrapper = document.querySelector(".minicart__wrapper");
    if (miniCartWrapper) miniCartWrapper.remove();

    cartTriggerElements.forEach((el) => {
        el.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.openMyCart) window.openMyCart(true);
        });
    });

    document.addEventListener(EMIT_CART_CLOSED, () => {
        document.documentElement.style.overflow = "auto";
    });
};
export const aussieGolfShopChanges = () => {
    document.addEventListener(EMIT_CART_CLOSED, () => {
        document.documentElement.classList.remove("js-drawer-open", "lock-scroll");
    });
};
export const laeniaShopChanges = () => {
    document.addEventListener(EMIT_CART_CLOSED, () => {
        document.body.style.overflow = "auto";
    });
};
export const puurbottleChanges = () => {
    document.addEventListener(EMIT_CART_OPENED, () => {
        document.body.style.pointerEvents = "all";
    });
};
export const trandafirShopChanges = () => {
    const cartTriggerElements = document.querySelectorAll(".header-actions__cart-icon");

    const miniCartWrapper = document.querySelector(".cart-drawer__dialog");
    if (miniCartWrapper) miniCartWrapper.remove();

    cartTriggerElements.forEach((el) => {
        el.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.openMyCart) window.openMyCart(true);
        });
    });

    document.addEventListener(EMIT_CART_CLOSED, () => {
        document.body.style.position = "unset";
    });
};

export const getProductAsBundleVariantId = (bundleConfig: any) => {
    if (window.kefi_bundles.shop == "d2f1bd.myshopify.com") {
        const id = (document.querySelector("#product_getVariant_id") as HTMLInputElement)?.value;
        if (id) return id;
    }

    return bundleConfig.bundle_as_product.variant_id;
};

export const atulyamChanges = () => {
    const hideCartElements = () => {
        const elements = document.querySelectorAll(".cart-drawer dialog");
        elements.forEach((el) => {
            (el as HTMLElement).style.setProperty("display", "none", "important");
            if (el.tagName === "DIALOG" && (el as any).open) {
                try {
                    (el as any).close();
                } catch (e) {
                    // ignore
                }
            }
        });
    };

    hideCartElements();

    const observer = new MutationObserver(() => hideCartElements());
    observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["open", "style", "class"],
    });
};

export const jardoshStudioChanges = () => {
    const cartTriggerElements = document.querySelectorAll(".header-actions__cart-icon");
    const miniCartWrapper1 = document.querySelector(".cart-drawer__dialog");
    if (miniCartWrapper1) miniCartWrapper1.remove();

    const miniCartWrapper2 = document.querySelector("#cart-drawer");
    if (miniCartWrapper2) miniCartWrapper2.remove();

    cartTriggerElements.forEach((el) => {
        el.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.openMyCart) window.openMyCart(true);
            document.querySelectorAll(".page-wrapper--drawer-open").forEach((ele) => {
                if (ele.classList.contains("page-wrapper--drawer-open")) {
                    ele.classList.remove("page-wrapper--drawer-open");
                }
            });
        });
    });

    document.addEventListener(EMIT_CART_CLOSED, () => {
        document.body.style.position = "unset";
    });
};

export function isIsolatedRenderingRequired() {
    let flag = false;

    if (window.kefi_bundles.shop === "reyandi.myshopify.com") flag = true;

    return flag;
}

export async function addAutoFreeGift() {
    try {
        const allBundles = window.kefi_bundles?.bundles_data?.allBundles || [];
        if (!Array.isArray(allBundles)) return;
        // Find eligible MixMatch bundles that have free gifts enabled
        const eligibleBundles = allBundles.filter(
            (bundle) =>
                bundle?.bundle_type === BundleTypes.MIX_MATCH &&
                bundle?.mixmatch_product_selection == "all" &&
                bundle?.discount_options?.some(
                    (opt: any) => opt.allow_free_gifts === true && opt.free_gifts && opt.free_gifts.length > 0,
                ),
        );
        if (eligibleBundles.length === 0) {
            console._log("No eligible bundles for free gift.");
            return;
        }
        // Fetch current cart
        const cartRes = await fetch("/cart.js?silent");
        const cart = await cartRes.json();
        const cartItemCount = cart.item_count || 0;
        // Loop through eligible bundles
        for (const bundle of eligibleBundles) {
            const discounts = bundle.discount_options || [];
            for (const discount of discounts) {
                // Skip if this discount doesn't allow free gifts
                if (!discount.allow_free_gifts) continue;
                const minQty = discount.quantity || 0;
                const maxQty = discount?.allowExtraItems ? Infinity : discount.maxQuantity;
                // Check if cart qualifies based on total item count
                if (cartItemCount < minQty || cartItemCount > maxQty) continue;
                // Determine gifts (from discount or bundle)
                const gifts = discount.free_gifts?.length ? discount.free_gifts : [];
                for (const gift of gifts) {
                    const giftId = gift?.variants[0]?.id?.split("/")?.pop();
                    if (!giftId) continue;
                    // Avoid duplicates
                    const alreadyInCart = cart.items.some((item: any) => item.variant_id.toString() === giftId);
                    if (alreadyInCart) continue;
                    // Add free gift to cart
                    console._log(`Adding free gift: ${gift.title || giftId}`);
                    const cartElement: any =
                        document.querySelector("cart-drawer") || document.querySelector("cart-notification");
                    const formData = {
                        items: [
                            {
                                id: giftId,
                                quantity: gift.quantity || 1,
                                // properties: { __free_gift: "true" },
                            },
                        ],
                        sections: [],
                    };
                    if (cartElement && cartElement.getSectionsToRender) {
                        formData.sections = cartElement
                            .getSectionsToRender()
                            .map((s: any) => s.section || s.id || s.selector);
                    }
                    const res = await fetch("/cart/add.js?silent", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(formData),
                    });
                    const data = await res.json();
                    openStoreCart(data);
                }
            }
        }
    } catch (error) {
        console.error("Error in addAutoFreeGift:", error);
    }
}

export function runCustomCodeForUser() {
    if (window.kefi_bundles.shop !== "4n0qqm-p9.myshopify.com") return;

    if (document.getElementById("kefi-hide-style")) return;

    const style = document.createElement("style");
    style.id = "kefi-hide-style";

    style.textContent = `
        pickup-availability-drawer,
        #PopupModal-popup,
        [id^="ProductModal-"][id$="__main"] {
            display: none !important;
        }
    `;

    document.head.appendChild(style);
}
